import {
  type Access,
  accessSchema,
  type CreatedAccessInvite,
  createdAccessInviteSchema,
  type PeopleList,
  peopleListSchema,
} from "@crux/contracts";
import { z } from "zod";
import { problem } from "./errors.js";

/**
 * The People screen's calls, and the caller's own access.
 *
 * These routes were written after the OpenAPI document stopped being generated
 * (`packages/contracts/openapi.json`, emitted by the Node backend until it was
 * removed). So these are plain requests whose answers are parsed with the
 * shared schemas in `@crux/contracts`: nothing is trusted unparsed, and the
 * schema is the one the server's shapes are written against
 * (`members/schema.rs`). When the document comes from Rust, they move onto the
 * typed client.
 */

const refusalSchema = z.object({ message: z.string() });

async function send(
  workspaceId: string,
  path: string,
  init: { readonly method: string; readonly body?: unknown },
): Promise<Response> {
  return await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}${path}`, {
    method: init.method,
    credentials: "same-origin",
    headers: init.body === undefined ? {} : { "content-type": "application/json" },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });
}

/**
 * A refusal in the server's own words when it gave some — the last owner, a
 * brand that is not a report here — and the status as a sentence otherwise.
 */
async function refused(response: Response, attempt: string): Promise<Error> {
  if (response.status === 400 || response.status === 409) {
    const parsed = refusalSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) return new Error(parsed.data.message);
  }
  return new Error(problem(response.status, attempt));
}

export async function fetchPeople(workspaceId: string): Promise<PeopleList> {
  const response = await send(workspaceId, "/people", { method: "GET" });
  if (!response.ok) throw await refused(response, "load the people");
  return peopleListSchema.parse(await response.json());
}

/** What the signed-in person may see and change in this workspace. */
export async function fetchMyAccess(workspaceId: string): Promise<Access> {
  const response = await send(workspaceId, "/people/me", { method: "GET" });
  if (!response.ok) throw await refused(response, "read your access");
  return accessSchema.parse(await response.json());
}

export async function inviteWithAccess(
  workspaceId: string,
  input: { readonly email: string; readonly access: Access },
): Promise<CreatedAccessInvite> {
  const response = await send(workspaceId, "/people/invites", {
    method: "POST",
    body: input,
  });
  if (response.status === 429) {
    throw new Error("Too many invites at once. Wait fifteen minutes and try again.");
  }
  if (!response.ok) throw await refused(response, "send the invite");
  return createdAccessInviteSchema.parse(await response.json());
}

export async function saveAccess(
  workspaceId: string,
  userId: string,
  access: Access,
): Promise<void> {
  const response = await send(
    workspaceId,
    `/people/${encodeURIComponent(userId)}/access`,
    { method: "PUT", body: { access } },
  );
  if (!response.ok) throw await refused(response, "save the access");
}
